import { QueueSettings } from '@prisma/client';
import { useMutation } from '@tanstack/react-query';
import { ValidationError } from 'yup';
import { updateQueueSettings } from '../../services/queue';
import { QueueSettingsPayload, queueSettingsSchema } from '../../types/queue';
import useUser from '../global/useUser';

interface Options {
  onSuccess?: (result: QueueSettings | null) => void | Promise<void>;
  onError?: (error: string) => void | Promise<void>;
  onMutate?: () => void | Promise<void>;
  onSettled?: () => void | Promise<void>;
}

function useUpdateQueueSettings({
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: Options = {}) {
  const { user, reloadUser } = useUser();

  return useMutation({
    mutationFn: async (payload: QueueSettingsPayload) => {
      if (!user?.business?.id) {
        throw new Error('No business ID found');
      }

      const body = await queueSettingsSchema.noUnknown().validate(payload, {
        stripUnknown: true,
      });

      const result = await updateQueueSettings(user.business.id, body);

      return result;
    },
    onSuccess: async result => {
      if (!result) return;

      await reloadUser();

      await onSuccess?.(result);
    },
    onError: (error: Error) => {
      if (error instanceof ValidationError) {
        onError?.(error.message);
        return;
      }
      onError?.('Failed to update queue settings');
    },
    onMutate,
    onSettled,
  });
}

export default useUpdateQueueSettings;
